import { ref } from 'vue';
import { getCurrentLocale } from '@/utils/i18n';

let initialized = false;

export function useAppInit() {
    const isLoading = ref(false);
    const siteStore = useSiteStore();
    const categoryStore = useCategoryStore();
    const siteApi = useSite();
    const categoryApi = useCategory();

    async function initApp() {
        if (initialized) {
            return;
        }

        isLoading.value = true;
        try {
            // Attendre que window.env soit chargé
            await waitForWindowEnv();

            i18n.global.locale.value = getCurrentLocale();
            await getAndSetSite();
            // Traductions et catégories sont indépendantes l'une de l'autre : on les
            // charge en parallèle (au lieu de les sérialiser) pour réduire le temps
            // d'ouverture. Chacune gère ses propres erreurs sans bloquer l'autre.
            await Promise.all([getTranslation(), getAndSetCategories()]);
            initialized = true;
        } catch (error) {
            console.error('Erreur d\'init de l\'app :', error);
            initialized = false;
        } finally {
            isLoading.value = false;
        }
    }

    // Fonction pour attendre que window.env soit chargé
    async function waitForWindowEnv(): Promise<void> {
        return new Promise((resolve) => {
            const checkEnv = () => {
                if (typeof window !== 'undefined' && window.env) {
                    resolve();
                } else {
                    setTimeout(checkEnv, 100);
                }
            };
            checkEnv();
        });
    }

    async function getAndSetSite() {
        const { data, error } = await siteApi.getSiteInfos();

        if (error.value) {
            console.error('❌ [getAndSetSite] Erreur API:', error.value);
            throw new Error('Site introuvable');
        }
        if (data.value) {
            siteStore.setSite(data.value);
            updateCSSVariables(siteStore);
            // Rattache la session à la boutique : en multi-tenant, des métriques
            // non regroupées mélangent toutes les boutiques et ne répondent à
            if (siteStore.site?.id) {
                identifySite(String(siteStore.site.id), { name: siteStore.site.name });
                registerAnalyticsContext({
                    site_id: String(siteStore.site.id),
                    site_name: siteStore.site.name,
                    ui_mode: 'new'
                });
            }
            if (siteStore.site.configurationUrl) {
                await siteStore.setConfiguration(siteStore.site.configurationUrl);
            }
        }
    }

    async function getTranslation() {
        if (!siteStore.site?.translationUrl) {
            return;
        }
        const VITE_BACK_URL = window.env.VITE_BACK_URL;
        const translationUrl = `${VITE_BACK_URL}/downloadSvg?filename=${siteStore.site.translationUrl}`;
        await fetchAndMergeTranslations(translationUrl);
    }

    async function getAndSetCategories() {
        const { data, error } = await categoryApi.getProductsGroupByCategories();
        if (error.value) {
            throw new Error('Erreur de récupération des catégories');
        }
        if (data.value) {
            categoryStore.setCategories(data.value);
        }
    }

    return { initApp, isLoading };
}
